<template>
  <v-form ref="form" >
    <v-container class="addressContainer">
      <div class="addressText">
        <div class="addressTextTitle">
          Billing Address
        </div>
        <div class="addressTextInfo" v-if="requirePayment && type !== 'pcw'">
          Please complete the required information
          below and click “Save & Request Quote”.
        </div>
        <div class="addressTextInfo" v-else-if="!requirePayment && type !== 'pcw'">
          Please complete the required information
          below and click “Update Address”.
        </div>
        <div class="addressTextInfo noMargin" v-else-if="type === 'pcw'">
          Enter your full address. All fields are required.
        </div>
      </div>
      <v-layout row wrap class="form">
        <v-flex x12>
          <v-layout row wrap>
            <v-flex xs12 md6 lg6 v-if="type !== 'pcw'">
              <v-text-field
                class="addressInputField"
                :class="{
                  'mr-2': $vuetify.breakpoint.mdAndUp,
                }"
                :value="firstName"
                label="First Name"
                name="first-name"
                @input="$emit('update:firstName', $event)"
                :rules="[rules.required]"
                outline
              ></v-text-field>
            </v-flex>
            <v-flex xs12 md6 lg6 v-if="type !== 'pcw'">
              <v-text-field
                class="addressInputField"
                :value="lastName"
                label="Last Name"
                name="last-name"
                @input="$emit('update:lastName', $event)"
                :rules="[rules.required]"
                outline
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex xs12 sm12 md6>
              <v-autocomplete
                class="addressInputField"
                :class="{
                  'mr-2': $vuetify.breakpoint.mdAndUp,
                }"
                :value="country"
                item-text="countryName"
                item-value="countryShortCode"
                :items="countriesList"
                @input="updateCountryClearState"
                label="Country"
                name="country"
                :rules="[rules.required]"
                outline
              ></v-autocomplete>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs12 md6 lg6>
              <v-text-field
                class="addressInputField"
                :class="{
                  'mr-2': $vuetify.breakpoint.mdAndUp,
                }"
                :value="address"
                label="Address"
                name="address"
                @input="$emit('update:address', $event)"
                :rules="[rules.required]"
                outline
              ></v-text-field>
            </v-flex>
            <v-flex xs12 md6 lg6>
              <v-text-field
                class="addressInputField"
                :value="city"
                label="City/Town"
                name="city"
                :rules="[rules.required]"
                @input="$emit('update:city', $event)"
                outline
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
 <v-flex xs6 md6 lg6>
  <v-menu
    content-class="statePopOver"
    offset-y
  >
    <template v-slot:activator="{ on, attrs }">
      <div  v-bind="attrs"
          v-on="on">
        <v-text-field
          :value="selectedRegion"
          append-icon="arrow_drop_down"
          label="State/Province/Region"
          readonly
          outline
          class="stateContainer"
          :class="{
            'mr-2': $vuetify.breakpoint.smAndUp,
          }"
        ></v-text-field>
      </div>
    </template>
    <v-list :style="{
    'max-height': '300px', 'overflow-y': statesFromCountry.length === 0 ? 'unset' : 'scroll'
    }">
      <v-list-tile v-if="statesFromCountry.length === 0" class="stateEmptyItem" >
        <v-list-tile-title >
          No selection available.
        </v-list-tile-title>
      </v-list-tile>

      <div v-else
        v-for="item in statesFromCountry"
        :key="item.name"
      >
        <v-subheader
          v-if="item.children"
        >
          {{ item.name }}
        </v-subheader>
        <v-list-tile
          v-else
          @click="regionClick(item)"
        >
          <v-list-tile-content>
            <v-list-tile-title>{{ item.name }}e</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <span v-if="item.children">
          <v-list-tile @click="regionClick(child)"
          v-for="child in item.children"
          :key="child.name"
          >
            <v-list-tile-content>
              <v-list-tile-title>
                {{ child.name }}
              </v-list-tile-title>
            </v-list-tile-content>
        </v-list-tile>
      </span>
      </div>
    </v-list>
  </v-menu>
</v-flex>
    <v-flex xs6 md6 lg6>
      <v-text-field
        class="addressInputField"
        :value="zipCode"
        label="Zip/Postal Code"
        name="zipCode"
        @input="$emit('update:zipCode', $event)"
        :rules="[rules.required]"
        outline
      ></v-text-field>
    </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>

import { getCountriesList, getCountrySubdivisions, getChildSubdivisionMatchingCountryAndState } from '../utils/location';

export default {
  props: {
    firstName: {
      type: String,
      default: '',
    },
    lastName: {
      type: String,
      default: '',
    },
    country: {
      type: String,
      default: '',
    },
    state: {
      type: String,
      default: '',
    },
    address: {
      type: String,
      default: '',
    },
    city: {
      type: String,
      default: '',
    },
    zipCode: {
      type: String,
      default: '',
    },
    isBillingAddressComplete: {
      type: Boolean,
      default: false,
    },
    requirePayment: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    rules: {
      required: value => !!value || 'Required.',
    },
    countriesList: getCountriesList().map(country => ({
      countryName: country.name,
      countryShortCode: country.alpha2,
    })),
    selectedRegion: '',
  }),
  mounted() {
    this.$nextTick(() => {
      const matchedState = getChildSubdivisionMatchingCountryAndState(this.country, this.state);
      this.selectedRegion = matchedState ? matchedState.name : '';
      this.checkFormCompleteness();
    });
  },
  methods: {
    updateCountryClearState($event) {
      this.$emit('update:country', $event);
      this.$emit('update:state', '');
      this.selectedRegion = '';
    },
    regionClick(region) {
      this.selectedRegion = region.name;
      this.$emit('update:state', region.code.slice(3));
    },
    checkFormCompleteness() {
      if (this.isFormCompleted) {
        const isValid = this.$refs.form.validate();
        this.$emit('update:isBillingAddressComplete', isValid);
      } else {
        this.$emit('update:isBillingAddressComplete', false);
      }
    },
  },
  computed: {
    statesFromCountry() {
      const statesList = getCountrySubdivisions(this.country);
      return statesList;
    },
    isFormCompleted() {
      if (
        this.firstName
        && this.lastName
        && this.country
        && this.address
        && this.zipCode
        && this.city
        && this.type !== 'pcw'
      ) {
        if ((this.state && this.statesFromCountry.length > 0)
        || (!this.state && this.statesFromCountry.length === 0)) {
          return true;
        }
      } else if (this.country
        && this.address
        && this.zipCode
        && this.city
        && this.type === 'pcw') {
        if ((this.state && this.statesFromCountry.length > 0)
        || (!this.state && this.statesFromCountry.length === 0)) {
          return true;
        }
      }
      return false;
    },
  },
  watch: {
    firstName() {
      this.checkFormCompleteness();
    },
    lastName() {
      this.checkFormCompleteness();
    },
    country() {
      this.checkFormCompleteness();
    },
    zipCode() {
      this.checkFormCompleteness();
    },
    state() {
      this.checkFormCompleteness();
    },
    city() {
      this.checkFormCompleteness();
    },
    address() {
      this.checkFormCompleteness();
    },
  },
};
</script>

<style>
.statePopOver {
  margin-top: -26px;
}

.stateContainer {
  font-Size: 16px;
  font-weight: 500;
  color: black !important;
  justify-content: center;
}

.addressContainer {
  padding-bottom: 0px !important;
  padding-top: 25px;
}

.addressTextTitle {
  font-family: Gotham;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
  color: #333333;
}

.addressTextInfo {
  font-family: Gotham;
  font-size: 16px;
  text-align: left;
  color: #000000;
  margin-bottom: 10px;
}

.noMargin {
  margin: 0px !important;
}

.addressInputField {
  font-family: Gotham;
  font-size: 16px;
  color: #999999;
}

/* Extra large Break Point */
@media screen and (min-width: 1904px) {
}

/* Tablet Break Point */
@media screen and (max-width: 1264px) {
}

/* Mid Phone to Tablet Break Point */
@media screen and (min-width: 767px) and (max-width: 959px) {
}

/* Mobile Break Point */
@media screen and (max-width: 767px) {
  .addressText {
    padding-top: 10px;
  }
}
</style>
